import { Formik } from 'formik';
import { useCallback } from 'react';
import { object, string } from 'yup';

import type { Thread, ThreadSystem } from '@frontend/api';
import { RUNES_CALLOUT_TYPES, THREAD_SYSTEMS } from '@frontend/api';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
  Input,
  Flex,
} from '@frontend/components';

import { SubFormProps } from '../types';
import { SubFormWrapper } from './shared/SubFormWrapper';
import { createRequiredSelectField } from './shared/validation';

type ThreadFormValues = {
  threadSystem: ThreadSystem;
  size: string;
};

const initialValues: ThreadFormValues = {
  threadSystem: THREAD_SYSTEMS.METRIC,
  size: '',
};

const THREAD_SYSTEM_OPTIONS = Object.values(THREAD_SYSTEMS).map((value) => ({
  value,
  label: value.toUpperCase(),
}));

const validationSchema = object().shape({
  threadSystem: createRequiredSelectField('Thread system'),
  size: string().required('Size is required'),
});

export const ThreadForm = ({
  onSubmit,
  onCancel,
  renderSelectedType,
}: SubFormProps) => {
  const handleSubmit = useCallback(
    (values: ThreadFormValues) => {
      const rawThreadStr = `${
        values.size
      } ${values.threadSystem.toUpperCase()}`;
      const thread: Thread = {
        calloutType: RUNES_CALLOUT_TYPES.THREAD,
        threadSystem: values.threadSystem,
        size: values.size,
        rawThreadStr,
      };

      onSubmit(thread, rawThreadStr);
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => {
        const selectedOption = THREAD_SYSTEM_OPTIONS.find(
          (option) => option.value === values.threadSystem,
        );

        return (
          <SubFormWrapper
            isValid={isValid}
            isSubmitting={isSubmitting}
            onCancel={onCancel}
            renderSelectedType={renderSelectedType}
            submitLabel="Add Thread"
          >
            <Flex direction="column" gap={1} px={1} pb={1}>
              <FormControl>
                <FormLabel>Thread System</FormLabel>
                <Select
                  name="threadSystem"
                  options={THREAD_SYSTEM_OPTIONS}
                  value={selectedOption}
                  onChange={(
                    option: { value: ThreadSystem; label: string } | null,
                  ) => {
                    if (option?.value) {
                      setFieldValue('threadSystem', option.value);
                    }
                  }}
                  size="sm"
                  isClearable={false}
                />
                {errors.threadSystem && touched.threadSystem && (
                  <FormErrorMessage>{errors.threadSystem}</FormErrorMessage>
                )}
              </FormControl>
              <FormControl>
                <FormLabel>Size</FormLabel>
                <Input
                  name="size"
                  value={values.size}
                  onChange={(e) => setFieldValue('size', e.target.value)}
                  size="sm"
                  placeholder="e.g., #1, 1/4, 8"
                />
                {errors.size && touched.size && (
                  <FormErrorMessage>{errors.size}</FormErrorMessage>
                )}
              </FormControl>
            </Flex>
          </SubFormWrapper>
        );
      }}
    </Formik>
  );
};
